import { apiRequest } from '../../../shared/services/apiClient';
import { Conversation } from '../types/Conversation';
import { getConversationById } from './conversationService';

const pickSingle = (payload: unknown): Record<string, unknown> => {
  if (!payload || typeof payload !== 'object') {
    return {};
  }

  const envelope = payload as Record<string, unknown>;
  if (envelope.data && typeof envelope.data === 'object' && !Array.isArray(envelope.data)) {
    return envelope.data as Record<string, unknown>;
  }

  if (envelope.item && typeof envelope.item === 'object' && !Array.isArray(envelope.item)) {
    return envelope.item as Record<string, unknown>;
  }

  return envelope;
};

const asString = (value: unknown): string => (typeof value === 'string' ? value : '');

const asOptionalString = (value: unknown): string | undefined => {
  const text = asString(value).trim();
  return text || undefined;
};

export interface WhatsAppAppGroupMember {
  jid: string;
  phoneNumber?: string;
  name?: string;
  admin?: string;
  participantId?: string;
  participantType?: string;
  contactId?: string;
}

export interface WhatsAppAppGroup {
  conversationId: string;
  channelAccountId: string;
  groupJid: string;
  title?: string;
  pictureUrl?: string;
  description?: string;
  members: WhatsAppAppGroupMember[];
}

export interface WhatsAppAppGroupInvite {
  conversationId: string;
  groupJid: string;
  inviteCode?: string;
  inviteUrl?: string;
}

const normalizeMember = (value: unknown): WhatsAppAppGroupMember | null => {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const item = value as Record<string, unknown>;
  const jid = asString(item.jid).trim();
  if (!jid) {
    return null;
  }

  return {
    jid,
    phoneNumber: asOptionalString(item.phoneNumber),
    name: asOptionalString(item.name),
    admin: asOptionalString(item.admin),
    participantId: asOptionalString(item.participantId),
    participantType: asOptionalString(item.participantType),
    contactId: asOptionalString(item.contactId),
  };
};

const normalizeGroup = (value: unknown): WhatsAppAppGroup => {
  const item = pickSingle(value);
  return {
    conversationId: asString(item.conversationId),
    channelAccountId: asString(item.channelAccountId),
    groupJid: asString(item.groupJid),
    title: asOptionalString(item.title),
    pictureUrl: asOptionalString(item.pictureUrl),
    description: asOptionalString(item.description),
    members: Array.isArray(item.members)
      ? item.members.map(normalizeMember).filter((member): member is WhatsAppAppGroupMember => Boolean(member))
      : [],
  };
};

const normalizeInvite = (value: unknown): WhatsAppAppGroupInvite => {
  const item = pickSingle(value);
  return {
    conversationId: asString(item.conversationId),
    groupJid: asString(item.groupJid),
    inviteCode: asOptionalString(item.inviteCode),
    inviteUrl: asOptionalString(item.inviteUrl),
  };
};

export const createWhatsAppAppGroup = async (input: {
  channelAccountId: string;
  subject: string;
  participants: string[];
  description?: string;
}): Promise<Conversation> => {
  const payload = await apiRequest<unknown>('/api/v1/whatsapp/app/groups', {
    method: 'POST',
    body: JSON.stringify({
      channelAccountId: input.channelAccountId,
      subject: input.subject,
      participants: input.participants,
      description: input.description,
    }),
  });
  const created = normalizeGroup(payload);
  const conversation = await getConversationById(created.conversationId);
  if (!conversation) {
    throw new Error('Created WhatsApp group conversation could not be loaded.');
  }

  return conversation;
};

export const getWhatsAppAppGroup = async (conversationId: string): Promise<WhatsAppAppGroup> => {
  const payload = await apiRequest<unknown>(`/api/v1/whatsapp/app/groups/${conversationId}`);
  return normalizeGroup(payload);
};

export const renameWhatsAppAppGroup = async (conversationId: string, subject: string): Promise<void> => {
  await apiRequest(`/api/v1/whatsapp/app/groups/${conversationId}/subject`, {
    method: 'POST',
    body: JSON.stringify({ subject }),
  });
};

export const updateWhatsAppAppGroupParticipants = async (
  conversationId: string,
  action: 'add' | 'remove' | 'promote' | 'demote',
  participants: string[],
): Promise<void> => {
  await apiRequest(`/api/v1/whatsapp/app/groups/${conversationId}/participants`, {
    method: 'POST',
    body: JSON.stringify({ action, participants }),
  });
};

export const getWhatsAppAppGroupInvite = async (conversationId: string): Promise<WhatsAppAppGroupInvite> => {
  const payload = await apiRequest<unknown>(`/api/v1/whatsapp/app/groups/${conversationId}/invite`);
  return normalizeInvite(payload);
};

export const revokeWhatsAppAppGroupInvite = async (conversationId: string): Promise<void> => {
  await apiRequest(`/api/v1/whatsapp/app/groups/${conversationId}/invite/revoke`, {
    method: 'POST',
  });
};

export const sendWhatsAppAppGroupInvite = async (
  conversationId: string,
  numbers: string[],
  description?: string,
): Promise<void> => {
  await apiRequest(`/api/v1/whatsapp/app/groups/${conversationId}/invite/send`, {
    method: 'POST',
    body: JSON.stringify({ numbers, description }),
  });
};

export const leaveWhatsAppAppGroup = async (conversationId: string): Promise<void> => {
  await apiRequest(`/api/v1/whatsapp/app/groups/${conversationId}/leave`, {
    method: 'POST',
  });
};
